import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus, AlertCircle } from 'lucide-react';
import ConsentFields, { ageFromDob } from '../components/ConsentFields';
import PasswordInput from '../components/PasswordInput';
import SocialSignIn from '../components/SocialSignIn';
import SocialPendingStep from '../components/SocialPendingStep';
import { safeReturnPath, rememberReturnPath } from '../lib/returnPath';
import './AuthPages.css';

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  // WT-5: a return path (UC-B5's state.from, e.g. the club page a stranger
  // pressed "Join the club" on). Same-site paths only; none → My Clubs.
  const location = useLocation();
  const from = safeReturnPath(location.state?.from);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '' });
  const [dob, setDob] = useState('');
  const [consents, setConsents] = useState({ analytics: false, marketing: false, audience: false });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingSocial, setPendingSocial] = useState(null); // B-53

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // /my-clubs routes by membership: 1 club → straight in, several → picker,
  // none → the "no club yet" landing state (B-5) — never a dead end.
  // WT-5: with a return path, go there instead. The email still needs
  // confirming (B-65 gate, "Check your inbox" over that page), and the link
  // opens from the inbox in a fresh page, so keep the path for it too.
  const afterSignUp = (signedIn) => {
    if (!from) { navigate('/my-clubs', { replace: true }); return; }
    if (signedIn?.emailVerified !== true) rememberReturnPath(from, signedIn?.id);
    navigate(from, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    const age = ageFromDob(dob);
    if (age === null) { setError('Please enter your date of birth'); return; }
    if (age < 18) { setError('Marakin accounts are for adults aged 18 and over. A parent or guardian can add children and under-18s from their own account.'); return; }
    if (!termsAccepted) { setError('Please accept the Terms of Service to create your account.'); return; }
    setError(''); setLoading(true);
    try {
      const created = await register({ ...form, dateOfBirth: dob, consents, termsAccepted });
      afterSignUp(created);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally { setLoading(false); }
  };

  // B-53: Apple/Google. An existing account is simply signed in (and linked
  // when the provider confirmed the email); someone new gets the DOB + terms
  // step first. Either way /my-clubs takes it from there (or the return path).
  const toMyClubs = (signedIn) => afterSignUp(signedIn);

  if (pendingSocial) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-wrap">
            <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
          </div>
          <SocialPendingStep pending={pendingSocial} onDone={toMyClubs} onCancel={() => setPendingSocial(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        <h1>Join Marakin</h1>
        {/* WT-28: came from signed-out "Create club" (via Login). */}
        <p className="auth-subtitle">
          {from === '/create-club' ? 'Create your account, then set up your club' : 'Create your account to connect with your club'}
        </p>

        <SocialSignIn onSignedIn={toMyClubs} onNeedsProfile={setPendingSocial} dividerText="or sign up with your email" />

        {error && <div className="auth-error"><AlertCircle size={16} /> {error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label className="label">First name</label>
              <input className="input" placeholder="Stuart" value={form.firstName} onChange={set('firstName')} required />
            </div>
            <div className="form-group">
              <label className="label">Last name</label>
              <input className="input" placeholder="Smith" value={form.lastName} onChange={set('lastName')} required />
            </div>
          </div>
          <div className="form-group">
            <label className="label">Email</label>
            <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
          </div>
          <div className="form-group">
            <label className="label">Password</label>
            <PasswordInput placeholder="Min 8 characters" value={form.password} onChange={set('password')} required />
          </div>
          <div className="form-group">
            <label className="label">Phone <span className="optional">(optional)</span></label>
            <input className="input" type="tel" placeholder="+44 7700 900000" value={form.phone} onChange={set('phone')} />
          </div>
          <ConsentFields
            dob={dob}
            onDobChange={(e) => setDob(e.target.value)}
            consents={consents}
            onConsentsChange={setConsents}
            termsAccepted={termsAccepted}
            onTermsChange={setTermsAccepted}
          />
          <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : <><UserPlus size={16} /> Create account</>}
          </button>
        </form>

        <p className="auth-switch">Already have an account? <Link to="/login" state={from ? { from } : undefined}>Sign in</Link></p>
      </div>
    </div>
  );
};

export default RegisterPage;
